import { ChangeEvent, FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAlert } from 'react-alert';
import { useTranslation } from 'react-i18next';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import validator from 'validator';

import { passwordRules } from '../config/passwordRules';
import { useAuthState } from '../hooks/useAuthState';
import { Container } from 'react-bootstrap';

export const ChangePasswordPage = () => {
  const { t } = useTranslation();
  const alert = useAlert();

  const { user } = useAuthState();

  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [errors, setErrors] = useState<any>({});
  const [posted, setPosted] = useState<boolean>(false);

  const currentPasswordChanged = (ev: ChangeEvent<HTMLInputElement>) => {
    setCurrentPassword(ev.target.value);
    setErrors({});
  };

  const passwordChanged = (ev: ChangeEvent<HTMLInputElement>) => {
    setPassword(ev.target.value);
    setErrors({});
  };

  const passwordConfirmChanged = (ev: ChangeEvent<HTMLInputElement>) => {
    setPasswordConfirm(ev.target.value);
    setErrors({});
  };

  const onFormSubmit = (ev: FormEvent) => {
    ev.preventDefault();

    let validationErrors = {};
    if (currentPassword.length < 1) {
      validationErrors = { ...validationErrors, currentPassword: t('auth.password-missing') };
    }
    if (!validator.isStrongPassword(password, passwordRules)) {
      validationErrors = { ...validationErrors, password: t('auth.invalid-password', passwordRules) };
    } else if (password !== passwordConfirm) {
      validationErrors = { ...validationErrors, passwordConfirm: t('auth.password-not-matching') };
    }

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0 && user?.email) {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      reauthenticateWithCredential(user, credential)
        .then((userCredential) => {
          updatePassword(userCredential.user, password)
            .then(() => {
              setPosted(true);
            })
            .catch(() => {
              alert.error(t('unknown-error'));
            });
        })
        .catch((error) => {
          if (error.code === 'auth/wrong-password') {
            setErrors({ currentPassword: t('auth.wrong-password') });
          } else {
            alert.error(t('unknown-error'));
          }
        });
    }
  };

  return (
    <Container>
      <form onSubmit={onFormSubmit} noValidate>
        <h1 className='h3 mb-3'>{t('changepasswordpage.title')}</h1>
        {posted ? (
          <h2 className='h6'>
            <i className='fas fa-check me-2 text-success' />
            {t('changepasswordpage.posted')}
          </h2>
        ) : (
          <>
            <label className='d-flex mt-3'>{t('changepasswordpage.current-password')}</label>
            <input
              autoFocus
              required
              type='password'
              className={['form-control my-2', errors.currentPassword ? 'is-invalid' : null].filter((p) => p).join(' ')}
              placeholder={t('changepasswordpage.current-password')}
              value={currentPassword}
              onChange={currentPasswordChanged}
              autoComplete='current-password'
            />
            <div className='d-flex invalid-feedback' dangerouslySetInnerHTML={{ __html: errors.currentPassword }} />

            <label className='d-flex mt-3'>{t('password')}</label>
            <input
              required
              type='password'
              className={['form-control my-2', errors.password ? 'is-invalid' : null].filter((p) => p).join(' ')}
              placeholder={t('password-placeholder')}
              value={password}
              onChange={passwordChanged}
              autoComplete='new-password'
            />
            <div className='d-flex invalid-feedback' dangerouslySetInnerHTML={{ __html: errors.password }} />

            <label className='d-flex mt-3'>{t('changepasswordpage.confirm-password')}</label>
            <input
              required
              type='password'
              className={['form-control my-2', errors.passwordConfirm ? 'is-invalid' : null].filter((p) => p).join(' ')}
              placeholder={t('changepasswordpage.confirm-password')}
              value={passwordConfirm}
              onChange={passwordConfirmChanged}
              autoComplete='new-password'
            />
            <div className='d-flex invalid-feedback' dangerouslySetInnerHTML={{ __html: errors.passwordConfirm }} />

            <button className='btn btn-primary w-100 my-3' type='submit'>
              {t('resetpasswordpage.post')}
            </button>
          </>
        )}

        <Link to='/login'>{t('back')}</Link>
      </form>
    </Container>
  );
};
